import InputField from 'components/FormFields/InputField';
import { PayoutModalPropsType } from 'components/PayoutManagement/types';
import { addPayoutMethod, updatePayoutMethod } from 'constants/apiUrls';
import { PayoutMethodEnum } from 'enum/common';
import { useFormik } from 'formik';
import { getCountryCallingCode, isValidNumber, CountryCode } from 'libphonenumber-js';
import { useAppSelector } from 'hooks/useRedux';
import { useAuth } from 'hooks/useAuth';
import { http } from 'middleware/apiMiddleware';
import { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RootState } from 'store';
import * as Yup from 'yup';
import RadioField from 'components/FormFields/RadioField';
import PhoneInputWithFlag, { CountryOption } from 'components/PayoutManagement/PhoneInputWithFlag';
import {
  getEmailForSubmissionType,
  getInitialEmail,
  getInitialPhone,
  getPhoneForSubmissionType,
  getSubmissionType,
  PayoutSubmissionTypeEnum,
  UserPayoutDefaults,
} from 'components/PayoutManagement/forms/paypalZelleFormUtils';

interface IPaypalFormProps {
  data: PayoutModalPropsType;
  afterSubmit: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

const getCallingCode = (code: string): string => {
  try {
    return `+${getCountryCallingCode(code as CountryCode)}`;
  } catch {
    return '+1';
  }
};

const PaypalForm: React.FC<IPaypalFormProps> = ({ data, afterSubmit, setIsSubmitting }) => {
  const authContext = useAuth();
  const user = authContext?.user as Record<string, string> | undefined;
  const payoutData = data?.data;
  const isEdit = data?.type === 'edit';
  const selectedBusiness = useAppSelector((state: RootState) => state.businesses.business);

  const userDefaults: UserPayoutDefaults = useMemo(
    () => ({
      email: user?.email || '',
      phone: user?.phoneNumber || user?.phone_number || '',
      phoneCountryCode: user?.phoneCountryCode || user?.phone_country_code || '',
      phoneCountryExtension: user?.phoneCountryExtension || user?.phone_country_extension || '',
      phoneCountryName: user?.phoneCountryName || user?.phone_country_name || '',
    }),
    [user],
  );

  const [submissionType, setSubmissionType] = useState<PayoutSubmissionTypeEnum>(() =>
    getSubmissionType(isEdit, payoutData, userDefaults),
  );

  const defaultCountryCode = isEdit
    ? payoutData?.phoneCountryCode || selectedBusiness?.address?.countryShortCode || 'US'
    : userDefaults.phoneCountryCode || selectedBusiness?.address?.countryShortCode || 'US';

  const [selectedCountryOption, setSelectedCountryOption] = useState<CountryOption | null>(null);

  const phoneCountryExtension = useMemo(() => {
    if (isEdit && payoutData?.phoneCountryExtension && !selectedCountryOption) {
      return payoutData.phoneCountryExtension;
    }
    if (!isEdit && userDefaults.phoneCountryExtension && !selectedCountryOption) {
      return userDefaults.phoneCountryExtension;
    }
    return selectedCountryOption?.callingCode || getCallingCode(defaultCountryCode);
  }, [
    isEdit,
    payoutData?.phoneCountryExtension,
    userDefaults.phoneCountryExtension,
    selectedCountryOption,
    defaultCountryCode,
  ]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().when([], {
          is: () => submissionType === PayoutSubmissionTypeEnum.EMAIL,
          then: (schema) => schema.email('Invalid email format').required('Email is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
        phoneNumber: Yup.string().when([], {
          is: () => submissionType === PayoutSubmissionTypeEnum.PHONE,
          then: (schema) =>
            schema
              .required('Phone number is required')
              .test('is-valid-phone', 'Invalid phone number', (value) => {
                if (!value) return false;
                return isValidNumber(`${phoneCountryExtension}${value}`);
              }),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [submissionType, phoneCountryExtension],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: getInitialEmail(isEdit, payoutData, userDefaults),
      phoneNumber: getInitialPhone(isEdit, payoutData, userDefaults),
      phoneCountryName:
        (isEdit ? payoutData?.phoneCountryName : userDefaults.phoneCountryName) ||
        selectedBusiness?.address?.country ||
        '',
      phoneCountryCode: selectedCountryOption?.code || defaultCountryCode,
      phoneCountryExtension: phoneCountryExtension,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const payload =
          submissionType === PayoutSubmissionTypeEnum.EMAIL
            ? {
                email: values?.email,
              }
            : {
                phoneCountryExtension: phoneCountryExtension,
                phoneCountryName: selectedCountryOption?.name || selectedBusiness?.address?.country,
                phoneCountryCode: selectedCountryOption?.code || defaultCountryCode,
                phoneNumber: values?.phoneNumber,
                email: '',
              };
        if (isEdit) {
          const { error } = await http.patch(
            updatePayoutMethod(
              PayoutMethodEnum.PAYPAL,
              payoutData?.uuid || '',
              selectedBusiness?.uuid || '',
            ),
            payload,
          );
          if (error) {
            toast.error(error?.message || 'Failed to update payout method');
            return;
          }
        } else {
          const { error } = await http.post(
            addPayoutMethod(PayoutMethodEnum.PAYPAL, selectedBusiness?.uuid || ''),
            payload,
          );
          if (error) {
            toast.error(error?.message || 'Failed to add payout method');
            return;
          }
        }
        afterSubmit();
      } catch {
        toast.error('Unexpected error occurred while adding/updating payout method');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const {
    values,
    setFieldValue,
    errors,
    handleSubmit,
    touched,
    setErrors,
    setTouched,
    validateForm,
  } = formik;

  useEffect(() => {
    if (isEdit) {
      setSubmissionType(getSubmissionType(true, payoutData));
    }
  }, [isEdit, payoutData?.uuid, payoutData?.email, payoutData?.phoneNumber]);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionType]);

  const handleSubmissionTypeChange = (nextType: PayoutSubmissionTypeEnum) => {
    setSubmissionType(nextType);
    if (nextType === PayoutSubmissionTypeEnum.EMAIL) {
      setFieldValue('email', getEmailForSubmissionType(isEdit, payoutData, userDefaults));
      setFieldValue('phoneNumber', '');
    } else {
      setFieldValue('phoneNumber', getPhoneForSubmissionType(isEdit, payoutData, userDefaults));
      setFieldValue('email', '');
    }
    setErrors({});
    setTouched({});
  };

  return (
    <div>
      <form onSubmit={handleSubmit} id={PayoutMethodEnum.PAYPAL}>
        <RadioField
          containerClass={'flex flex-row'}
          value={submissionType}
          options={[
            { label: 'Email Address', value: PayoutSubmissionTypeEnum.EMAIL },
            { label: 'Phone Number', value: PayoutSubmissionTypeEnum.PHONE },
          ]}
          name={'paypalForm'}
          onChange={(value) => handleSubmissionTypeChange(value?.value)}
        />
        {submissionType === PayoutSubmissionTypeEnum.EMAIL ? (
          <InputField
            id="email"
            placeholder="Enter Email Address"
            onChange={(e) => {
              setFieldValue('email', e.target.value);
            }}
            value={values?.email}
            error={(touched.email && errors.email) || ''}
          />
        ) : (
          <PhoneInputWithFlag
            defaultCountryCode={defaultCountryCode}
            initialPhoneCountryExtension={
              isEdit ? payoutData?.phoneCountryExtension : userDefaults.phoneCountryExtension
            }
            phoneNumber={values?.phoneNumber || ''}
            onPhoneChange={(value) => setFieldValue('phoneNumber', value)}
            onCountryChange={(option) => {
              setSelectedCountryOption(option);
              setFieldValue('phoneCountryCode', option.code);
            }}
            error={errors.phoneNumber ? String(errors.phoneNumber) : undefined}
            touched={!!touched.phoneNumber}
          />
        )}
      </form>
    </div>
  );
};

export default PaypalForm;
